import React, { useState, useEffect, useRef, forwardRef, useImperativeHandle, useMemo } from 'react';
import {
  Box,
  Typography,
  Paper,
  CircularProgress,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Autocomplete,
  TextField,
  Chip,
  FormControlLabel,
  Checkbox
} from '@mui/material';
import { useTranslation } from 'react-i18next';
import { BarChart } from '@mui/x-charts/BarChart';
import jsPDF from 'jspdf';
import 'jspdf-autotable';
import html2canvas from 'html2canvas';
import { getAuthHeaders } from '../../../utils/authUtils';
import { createApiUrl } from '../../../utils/apiConfig';
import { useSnackbar } from '../../CentralSnackbar';
import { useFormatConfig, formatDateSync } from '../../../utils/dateTimeFormat';

const Regions = forwardRef(({ currentUser }, ref) => {
  const { t } = useTranslation();
  const { showError } = useSnackbar();
  const { isLoaded: isFormatConfigLoaded } = useFormatConfig();
  const chartRef = useRef(null);
  
  // State
  const [regionsData, setRegionsData] = useState(null);
  const [regionsLoading, setRegionsLoading] = useState(false);
  const [timeFilter, setTimeFilter] = useState('month'); // Default: Monat
  const [startPeriod, setStartPeriod] = useState('');
  const [endPeriod, setEndPeriod] = useState('');
  const [allUsers, setAllUsers] = useState([]);
  const [selectedUsers, setSelectedUsers] = useState([]);
  
  // Kategorien-Toggles
  const [showMyReports, setShowMyReports] = useState(true);
  const [showForeignSignedReports, setShowForeignSignedReports] = useState(true);
  const [showIndicationApprovals, setShowIndicationApprovals] = useState(true);
  const [showReleasedReports, setShowReleasedReports] = useState(true);
  
  // Highlighting State
  const [highlightedSeries, setHighlightedSeries] = useState(null);

  // Helper: Get current period based on timeFilter
  const getCurrentPeriod = (filterType) => {
    const now = new Date();
    switch (filterType) {
      case 'date':
        return now.toISOString().split('T')[0];
      case 'week': {
        const year = now.getFullYear();
        const oneJan = new Date(year, 0, 1);
        const numberOfDays = Math.floor((now - oneJan) / (24 * 60 * 60 * 1000));
        const weekNumber = Math.ceil((numberOfDays + oneJan.getDay() + 1) / 7);
        return `${year}-W${String(weekNumber).padStart(2, '0')}`;
      }
      case 'month':
        return `${now.getFullYear()}-${String(now.getMonth() + 1).padStart(2, '0')}`;
      case 'quarter': {
        const quarter = Math.ceil((now.getMonth() + 1) / 3);
        return `${now.getFullYear()}-Q${quarter}`;
      }
      case 'year':
        return `${now.getFullYear()}`;
      default:
        return '';
    }
  };

  // Helper: Get next/previous period
  const getAdjacentPeriod = (period, filterType, direction) => {
    if (!period) return period;
    
    switch (filterType) {
      case 'date': {
        const date = new Date(period);
        date.setDate(date.getDate() + direction);
        return date.toISOString().split('T')[0];
      }
      case 'week': {
        const [year, week] = period.split('-W').map(Number);
        let newWeek = week + direction;
        let newYear = year;
        
        if (newWeek < 1) {
          newYear--;
          newWeek = 52;
        } else if (newWeek > 52) {
          newYear++;
          newWeek = 1;
        }
        return `${newYear}-W${String(newWeek).padStart(2, '0')}`;
      }
      case 'month': {
        const [year, month] = period.split('-').map(Number);
        let newMonth = month + direction;
        let newYear = year;
        
        if (newMonth < 1) {
          newYear--;
          newMonth = 12;
        } else if (newMonth > 12) {
          newYear++;
          newMonth = 1;
        }
        return `${newYear}-${String(newMonth).padStart(2, '0')}`;
      }
      case 'quarter': {
        const [year, quarter] = period.split('-Q').map(Number);
        let newQuarter = quarter + direction;
        let newYear = year;
        
        if (newQuarter < 1) {
          newYear--;
          newQuarter = 4;
        } else if (newQuarter > 4) {
          newYear++;
          newQuarter = 1;
        }
        return `${newYear}-Q${newQuarter}`;
      }
      case 'year': {
        const year = parseInt(period);
        return `${year + direction}`;
      }
      default:
        return period;
    }
  };

  // Expose download functions to parent component via ref
  useImperativeHandle(ref, () => ({
    handlePdfDownload,
    handleCsvDownload
  }));

  // Set default period on mount
  useEffect(() => {
    const currentPeriod = getCurrentPeriod(timeFilter);
    setStartPeriod(currentPeriod);
    setEndPeriod(currentPeriod);
  }, []);

  // Wheel event for period scrolling (Shift + Scroll)
  useEffect(() => {
    const handleWheel = (event) => {
      if (event.shiftKey && (startPeriod || endPeriod)) {
        event.preventDefault();
        
        const direction = event.deltaY > 0 ? -1 : 1;
        
        setStartPeriod(prev => getAdjacentPeriod(prev, timeFilter, direction));
        setEndPeriod(prev => getAdjacentPeriod(prev, timeFilter, direction));
      }
    };

    window.addEventListener('wheel', handleWheel, { passive: false });
    
    return () => {
      window.removeEventListener('wheel', handleWheel);
    };
  }, [timeFilter, startPeriod, endPeriod]);

  // Load Users on mount
  useEffect(() => {
    loadUsers();
  }, []);

  // Load Regions data when selectedUsers change
  useEffect(() => {
    if (selectedUsers.length > 0) {
      loadRegionsData();
    }
  }, [selectedUsers]);

  // Get all available modalities from all users
  const availableRegions = useMemo(() => {
    if (!regionsData) return [];
    const modalities = new Set();
    Object.values(regionsData).forEach(userData => {
      if (userData.regions) {
        Object.keys(userData.regions).forEach(mod => modalities.add(mod));
      }
    });
    return Array.from(modalities).sort();
  }, [regionsData]);

  // Farben für Modalitäten (10 Farben aus CSS)
  const getModalityColor = (index) => {
    return `var(--rd-chart-${(index % 10) + 1})`;
  };

  // Calculate chart data
  // Calculate available periods based on regionsData
  const availablePeriods = useMemo(() => {
    const getAllPeriods = () => {
      const periods = new Set();
      
      if (!regionsData) return [];

      Object.values(regionsData).forEach(userData => {
        if (!userData || !userData.regions) return;

        const addPeriods = (data) => {
          if (data) {
            data.forEach(d => periods.add(d.period));
          }
        };
        
        // Durchlaufe alle Regionen des Users
        Object.values(userData.regions).forEach(modalityData => {
          if (!modalityData) return;
          
          // Für jede Kategorie die Perioden sammeln
          ['indication_approvals', 'my_reports', 'foreign_signed_reports', 'released_reports'].forEach(categoryKey => {
            if (!modalityData[categoryKey]) return;
            
            switch (timeFilter) {
              case 'date':
                addPeriods(modalityData[categoryKey].by_date);
                break;
              case 'week':
                addPeriods(modalityData[categoryKey].by_week);
                break;
              case 'month':
                addPeriods(modalityData[categoryKey].by_month);
                break;
              case 'quarter':
                addPeriods(modalityData[categoryKey].by_quarter);
                break;
              case 'year':
                addPeriods(modalityData[categoryKey].by_year);
                break;
              default:
                break;
            }
          });
        });
      });
      
      return Array.from(periods).sort().reverse();
    };
    
    return getAllPeriods();
  }, [regionsData, timeFilter]);

  const chartData = useMemo(() => {
    if (!regionsData || Object.keys(regionsData).length === 0) return [];

    const data = [];

    selectedUsers.forEach(user => {
      const userData = regionsData[user.id];
      if (!userData || !userData.regions) return;

      // Berechne für jede Kategorie die Summe pro Region
      const getCategoryCounts = (categoryKey) => {
        const counts = {};
        
        // Initialisiere alle Modalitäten mit 0
        availableRegions.forEach(mod => {
          counts[mod] = 0;
        });

        // Summiere die Counts für jede Region
        Object.entries(userData.regions).forEach(([modality, modalityData]) => {
          if (!modalityData[categoryKey]) return;

          let dataArray = [];
          switch (timeFilter) {
            case 'date': dataArray = modalityData[categoryKey].by_date; break;
            case 'week': dataArray = modalityData[categoryKey].by_week; break;
            case 'month': dataArray = modalityData[categoryKey].by_month; break;
            case 'quarter': dataArray = modalityData[categoryKey].by_quarter; break;
            case 'year': dataArray = modalityData[categoryKey].by_year; break;
            default: break;
          }

          // Filter nach Zeitraum
          if (startPeriod || endPeriod) {
            dataArray = dataArray.filter(item => {
              if (startPeriod && endPeriod) {
                return item.period >= startPeriod && item.period <= endPeriod;
              } else if (startPeriod) {
                return item.period >= startPeriod;
              } else if (endPeriod) {
                return item.period <= endPeriod;
              }
              return true;
            });
          }

          counts[modality] = dataArray.reduce((sum, item) => sum + item.count, 0);
        });

        return counts;
      };

      const indicationCounts = getCategoryCounts('indication_approvals');
      const myReportsCounts = getCategoryCounts('my_reports');
      const foreignSignedCounts = getCategoryCounts('foreign_signed_reports');
      const releasedCounts = getCategoryCounts('released_reports');

      // Erstelle 4 Datenpunkte (einen pro Kategorie) mit eindeutigem Label
      const categoryLabels = {
        'indication': t('statistics.personal.fallzahlen.indicationApprovals', 'Indikationsprüfungen'),
        'myReports': t('statistics.team.fallzahlen.createdReports', 'Erstellte Befunde'),
        'foreignSigned': t('statistics.personal.fallzahlen.foreignSignedReports', '(Fremd-)signierte Befunde'),
        'released': t('statistics.personal.fallzahlen.releasedReports', 'Freigegebene Befunde')
      };
      
      if (showIndicationApprovals) {
        data.push({
          id: `${user.id}-indication`,
          label: `${user.academic_degree || ''} ${user.first_name} ${user.last_name} - ${categoryLabels['indication']}`.trim(),
          userId: user.id,
          category: 'indication',
          ...indicationCounts
        });
      }
      
      if (showMyReports) {
        data.push({
          id: `${user.id}-myReports`,
          label: `${user.academic_degree || ''} ${user.first_name} ${user.last_name} - ${categoryLabels['myReports']}`.trim(),
          userId: user.id,
          category: 'myReports',
          ...myReportsCounts
        });
      }
      
      if (showForeignSignedReports) {
        data.push({
          id: `${user.id}-foreignSigned`,
          label: `${user.academic_degree || ''} ${user.first_name} ${user.last_name} - ${categoryLabels['foreignSigned']}`.trim(),
          userId: user.id,
          category: 'foreignSigned',
          ...foreignSignedCounts
        });
      }
      
      if (showReleasedReports) {
        data.push({
          id: `${user.id}-released`,
          label: `${user.academic_degree || ''} ${user.first_name} ${user.last_name} - ${categoryLabels['released']}`.trim(),
          userId: user.id,
          category: 'released',
          ...releasedCounts
        });
      }
    });

    return data;
  }, [regionsData, selectedUsers, availableRegions, timeFilter, startPeriod, endPeriod, 
      showIndicationApprovals, showMyReports, showForeignSignedReports, showReleasedReports, t]);

  // Apply SVG patterns to bars and add event listeners for highlighting
  useEffect(() => {
    const timeout = setTimeout(() => {
      if (!chartRef.current || !chartData || chartData.length === 0) return;

      console.log('=== Applying patterns to bars ===');
      console.log('Chart data length:', chartData.length);
      console.log('Available modalities:', availableRegions);
      
      // Gruppiere Balken nach Modalität (aus der CSS-Klasse)
      const barsByModality = new Map();
      
      availableRegions.forEach(modality => {
        const selector = `.MuiBarElement-series-${modality}`;
        const bars = chartRef.current.querySelectorAll(selector);
        if (bars.length > 0) {
          barsByModality.set(modality, Array.from(bars));
          console.log(`Found ${bars.length} bars for modality "${modality}"`);
        }
      });
      
      if (barsByModality.size === 0) {
        console.warn('No bars found for any modality! Retrying...');
        return;
      }
      
      // Wende Patterns an - jede Modalität hat chartData.length Balken (einen pro User-Kategorie)
      barsByModality.forEach((bars, modality) => {
        bars.forEach((bar, barIndex) => {
          // Finde den entsprechenden Datenpunkt basierend auf dem Index
          const dataPoint = chartData[barIndex];
          if (!dataPoint) {
            console.warn(`No data point for bar ${barIndex} of ${modality}`);
            return;
          }
          
          // Wende das entsprechende Pattern basierend auf der Kategorie an
          let patternId = '';
          switch (dataPoint.category) {
            case 'indication':
              patternId = `dots-${modality}`;
              break;
            case 'myReports':
              patternId = `horizontal-${modality}`;
              break;
            case 'foreignSigned':
              patternId = `diagonal-${modality}`;
              break;
            case 'released':
              patternId = `vertical-${modality}`;
              break;
          }
          
          if (patternId) {
            const patternUrl = `url(#${patternId})`;
            bar.setAttribute('fill', patternUrl);
            bar.style.fill = patternUrl;
            // Speichere die Kategorie für den Hover-Effekt (alle Balken mit gleicher Kategorie werden gehighlighted)
            bar.setAttribute('data-category', dataPoint.category);
            
            // Debug: Log erste paar Pattern-Zuweisungen
            if (barIndex < 2) {
              console.log(`Applied pattern to ${modality} bar ${barIndex}: ${patternUrl}, category: ${dataPoint.category}`);
            }
          }
        });
      });

      console.log('Patterns applied successfully!');

      // Event listeners für Highlighting - highlighte alle Balken mit gleicher Kategorie
      const allBars = chartRef.current.querySelectorAll('.MuiBarElement-root');
      
      const handleBarMouseEnter = (event) => {
        const target = event.currentTarget;
        const category = target.getAttribute('data-category');
        
        if (!category) return;
        
        allBars.forEach(bar => bar.classList.remove('highlighted'));
        
        // Highlighte alle Balken mit der gleichen Kategorie (z.B. alle "Indikationsprüfungen" aller User)
        allBars.forEach((bar) => {
          if (bar.getAttribute('data-category') === category) {
            bar.classList.add('highlighted');
          }
        });
        
        setHighlightedSeries(category);
      };

      const handleBarMouseLeave = () => {
        allBars.forEach(bar => bar.classList.remove('highlighted'));
        setHighlightedSeries(null);
      };

      allBars.forEach(bar => {
        bar.addEventListener('mouseenter', handleBarMouseEnter);
        bar.addEventListener('mouseleave', handleBarMouseLeave);
      });

      return () => {
        allBars.forEach(bar => {
          bar.removeEventListener('mouseenter', handleBarMouseEnter);
          bar.removeEventListener('mouseleave', handleBarMouseLeave);
        });
      };
    }, 500);

    return () => clearTimeout(timeout);
  }, [chartData, availableRegions]);

  const loadUsers = async () => {
    try {
      const response = await fetch(createApiUrl('users/'), {
        headers: { ...getAuthHeaders() }
      });
      
      if (response.ok) {
        const data = await response.json();
        const filteredUsers = data.filter(user => 
          user.group_level >= 1 && 
          user.group_level <= 5 && 
          user.is_active === true
        );
        setAllUsers(filteredUsers);
        setSelectedUsers(filteredUsers);
      } else {
        showError(t('statistics.team.loadUsersError', 'Fehler beim Laden der Benutzer'), 'ERR-STATS-TEAM-MOD-001');
      }
    } catch (error) {
      console.error('Error loading users:', error);
      showError(t('statistics.team.loadUsersError', 'Fehler beim Laden der Benutzer'), 'ERR-STATS-TEAM-MOD-002');
    }
  };

  const loadRegionsData = async () => {
    if (selectedUsers.length === 0) return;
    
    setRegionsLoading(true);
    try {
      const userIds = selectedUsers.map(u => u.id).join(',');
      
      const response = await fetch(createApiUrl(`statistics/team/regions?user_ids=${userIds}`), {
        headers: { ...getAuthHeaders() }
      });
      
      if (response.ok) {
        const data = await response.json();
        console.log('=== TEAM REGIONS: Received data:', data);
        setRegionsData(data.users);
      } else {
        console.error('=== TEAM REGIONS: Response not OK:', response.status, response.statusText);
        showError(t('statistics.team.regionen.loadError', 'Fehler beim Laden der Regionen'), 'ERR-STATS-TEAM-REG-003');
      }
    } catch (error) {
      console.error('Error loading Regions data:', error);
      showError(t('statistics.team.regionen.loadError', 'Fehler beim Laden der Regionen'), 'ERR-STATS-TEAM-REG-004');
    } finally {
      setRegionsLoading(false);
    }
  };

  const handleTimeFilterChange = (event) => {
    const newFilterType = event.target.value;
    setTimeFilter(newFilterType);
    const currentPeriod = getCurrentPeriod(newFilterType);
    setStartPeriod(currentPeriod);
    setEndPeriod(currentPeriod);
  };

  const handleStartPeriodChange = (event) => {
    setStartPeriod(event.target.value);
  };

  const handleEndPeriodChange = (event) => {
    setEndPeriod(event.target.value);
  };

  // CSV Export Function (simplified for now, we'll add later)
  const handleCsvDownload = () => {
    if (!regionsData || selectedUsers.length === 0) {
      showError(t('statistics.team.regionen.noData', 'Keine Daten vorhanden'), 'ERR-STATS-TEAM-REG-CSV-001');
      return;
    }

    // Header-Zeile
    const headers = [
      t('statistics.team.regionen.user', 'Benutzer'),
      t('statistics.team.regionen.region', 'Region'),
      t('statistics.team.fallzahlen.indicationCheck', 'Indikationsprüfungen'),
      t('statistics.team.fallzahlen.createdReports', 'Erstellte Befunde'),
      t('statistics.team.fallzahlen.signedReports', '(Fremd-)signierte Befunde'),
      t('statistics.team.fallzahlen.releasedReports', 'Freigegebene Befunde')
    ];
    
    const rows = [headers];

    // Für jeden User eine Zeile pro Region
    selectedUsers.forEach(user => {
      const userData = regionsData[user.id];
      if (!userData || !userData.regions) return;

      Object.entries(userData.regions).forEach(([region, regionData]) => {
        const row = [
          `${user.academic_degree || ''} ${user.first_name || ''} ${user.last_name || ''}`.trim(),
          region,
          regionData.indication_approvals?.total_count || 0,
          regionData.my_reports?.total_count || 0,
          regionData.foreign_signed_reports?.total_count || 0,
          regionData.released_reports?.total_count || 0
        ];
        rows.push(row);
      });
    });

    // CSV-String erstellen
    const csvContent = rows.map(row => row.map(cell => 
      typeof cell === 'string' && (cell.includes(',') || cell.includes('"')) 
        ? `"${cell.replace(/"/g, '""')}"` 
        : cell
    ).join(',')).join('\n');

    // Download
    const blob = new Blob(['\ufeff' + csvContent], { type: 'text/csv;charset=utf-8;' });
    const url = URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    link.download = 'team_regions_statistics.csv';
    link.click();
    URL.revokeObjectURL(url);
  };

  // PDF Export Function
  const handlePdfDownload = async () => {
    const doc = new jsPDF();
    const pageWidth = doc.internal.pageSize.getWidth();
    const pageHeight = doc.internal.pageSize.getHeight();
    
    // Hole Benutzerinformationen aus localStorage
    const userStr = localStorage.getItem('user');
    const currentUser = userStr ? JSON.parse(userStr) : null;
    const userName = currentUser 
      ? `${currentUser.academic_degree || ''} ${currentUser.first_name || ''} ${currentUser.last_name || ''}`.trim()
      : '';
    
    // Funktion für Fußzeile
    const addFooter = () => {
      const now = new Date();
      const dateStr = isFormatConfigLoaded ? formatDateSync(now.toISOString().split('T')[0]) : now.toLocaleDateString();
      const timeStr = now.toLocaleTimeString('de-DE', { hour: '2-digit', minute: '2-digit' });
      const footerText = `${t('statistics.team.regionen.pdfTitle', 'Team Report - Regionen')}, ${t('statistics.personal.pdf.createdBy', 'erstellt von')} ${userName}, ${t('statistics.personal.pdf.on', 'am')} ${dateStr} ${t('statistics.personal.pdf.at', 'um')} ${timeStr}`;
      
      doc.setFontSize(8);
      doc.setTextColor(128, 128, 128);
      doc.text(footerText, 14, pageHeight - 10);
      doc.setTextColor(0, 0, 0);
    };
    
    // Titel (linkszentriert)
    doc.setFontSize(16);
    doc.text(t('statistics.team.regionen.pdfTitle', 'Team Report - Regionen'), 14, 15);
    
    // Zeitraum-Info
    let yPosition = 25;
    doc.setFontSize(10);
    const timeFilterLabels = {
      'date': t('statistics.team.fallzahlen.day', 'Tag'),
      'week': t('statistics.team.fallzahlen.week', 'Woche'),
      'month': t('statistics.team.fallzahlen.month', 'Monat'),
      'quarter': t('statistics.team.fallzahlen.quarter', 'Quartal'),
      'year': t('statistics.team.fallzahlen.year', 'Jahr')
    };
    const filterText = `${t('statistics.team.fallzahlen.timePeriod', 'Zeitraum')}: ${timeFilterLabels[timeFilter] || timeFilter}`;
    doc.text(filterText, 14, yPosition);
    yPosition += 5;
    
    if (startPeriod || endPeriod) {
      const rangeText = `${startPeriod ? formatPeriod(startPeriod) : t('statistics.team.fallzahlen.all', 'Alle')} - ${endPeriod ? formatPeriod(endPeriod) : t('statistics.team.fallzahlen.all', 'Alle')}`;
      doc.text(rangeText, 14, yPosition);
      yPosition += 5;
    }
    
    yPosition += 5;
    
    // Für jeden ausgewählten User eine Tabelle
    selectedUsers.forEach((user, userIndex) => {
      const userData = regionsData[user.id];
      if (!userData || !userData.regions) return;
      
      // Prüfe ob neue Seite benötigt wird
      if (yPosition > 250) {
        addFooter(); // Fußzeile auf der alten Seite
        doc.addPage();
        yPosition = 15;
      }
      
      // User-Name als Überschrift
      doc.setFontSize(12);
      const userTableName = `${user.academic_degree || ''} ${user.first_name} ${user.last_name}`.trim();
      doc.text(userTableName, 14, yPosition);
      yPosition += 7;

      // Bereite Tabellendaten vor
      const tableData = [];
      
      // Berechne für jede Region die Summen
      Object.entries(userData.regions).forEach(([modality, modalityData]) => {
        const row = [modality];
        
        // Für jede Kategorie die Summe berechnen
        ['indication_approvals', 'my_reports', 'foreign_signed_reports', 'released_reports'].forEach(categoryKey => {
          if (!modalityData[categoryKey]) {
            row.push(0);
            return;
          }
          
          let dataArray = [];
          switch (timeFilter) {
            case 'date': dataArray = modalityData[categoryKey].by_date; break;
            case 'week': dataArray = modalityData[categoryKey].by_week; break;
            case 'month': dataArray = modalityData[categoryKey].by_month; break;
            case 'quarter': dataArray = modalityData[categoryKey].by_quarter; break;
            case 'year': dataArray = modalityData[categoryKey].by_year; break;
            default: break;
          }
          
          // Filter nach Zeitraum
          if (startPeriod || endPeriod) {
            dataArray = dataArray.filter(item => {
              if (startPeriod && endPeriod) {
                return item.period >= startPeriod && item.period <= endPeriod;
              } else if (startPeriod) {
                return item.period >= startPeriod;
              } else if (endPeriod) {
                return item.period <= endPeriod;
              }
              return true;
            });
          }
          
          const sum = dataArray.reduce((total, item) => total + item.count, 0);
          row.push(sum);
        });
        
        tableData.push(row);
      });
      
      // Tabelle nur hinzufügen, wenn Daten vorhanden sind
      if (tableData.length > 0) {
        doc.autoTable({
        startY: yPosition,
          head: [[
            t('statistics.personal.modalitaeten.modality', 'Modalität'),
            t('statistics.personal.fallzahlen.indicationApprovals', 'Indikationsprüfungen'),
            t('statistics.team.fallzahlen.createdReports', 'Erstellte Befunde'),
            t('statistics.personal.fallzahlen.foreignSignedReports', 'Signierte Befunde'),
            t('statistics.personal.fallzahlen.releasedReports', 'Freigegebene Befunde')
          ]],
          body: tableData,
          theme: 'grid',
          styles: { fontSize: 9, cellPadding: 2 },
          headStyles: { fillColor: [66, 66, 66], textColor: [255, 255, 255], fontStyle: 'bold' },
          margin: { left: 14, right: 14 },
        });
        
        yPosition = doc.lastAutoTable.finalY + 10;
      }
    });
    
    // Fußzeile auf der letzten Seite
    addFooter();
    
    // Speichern
    doc.save('team_regions_report.pdf');
  };

  // Prüfe ob User ausgewählt sind
  if (selectedUsers.length === 0) {
    return (
      <Box>
        <Typography variant="h6" gutterBottom>
          {t('statistics.team.categories.modalitaeten', 'Team-Modalitäten')}
        </Typography>

        {/* Filter und User-Auswahl in einer Reihe */}
        <Box sx={{ display: 'flex', gap: 2, mb: 3, alignItems: 'flex-start' }}>
          <FormControl sx={{ width: 120, flexShrink: 0 }} disabled>
            <InputLabel>{t('statistics.team.fallzahlen.timePeriod', 'Zeitraum')}</InputLabel>
            <Select
              value={timeFilter}
              onChange={handleTimeFilterChange}
              label={t('statistics.team.fallzahlen.timePeriod', 'Zeitraum')}
            >
              <MenuItem value="date">{t('statistics.team.fallzahlen.day', 'Tag')}</MenuItem>
              <MenuItem value="week">{t('statistics.team.fallzahlen.week', 'Woche')}</MenuItem>
              <MenuItem value="month">{t('statistics.team.fallzahlen.month', 'Monat')}</MenuItem>
              <MenuItem value="quarter">{t('statistics.team.fallzahlen.quarter', 'Quartal')}</MenuItem>
              <MenuItem value="year">{t('statistics.team.fallzahlen.year', 'Jahr')}</MenuItem>
            </Select>
          </FormControl>

          <FormControl sx={{ width: 120, flexShrink: 0 }} disabled>
            <InputLabel>{t('statistics.team.fallzahlen.startPeriod', 'Von')}</InputLabel>
            <Select
              value={startPeriod}
              label={t('statistics.team.fallzahlen.startPeriod', 'Von')}
            >
              <MenuItem value="">{t('statistics.team.fallzahlen.all', 'Alle')}</MenuItem>
            </Select>
          </FormControl>

          <FormControl sx={{ width: 120, flexShrink: 0 }} disabled>
            <InputLabel>{t('statistics.team.fallzahlen.endPeriod', 'Bis')}</InputLabel>
            <Select
              value={endPeriod}
              label={t('statistics.team.fallzahlen.endPeriod', 'Bis')}
            >
              <MenuItem value="">{t('statistics.team.fallzahlen.all', 'Alle')}</MenuItem>
            </Select>
          </FormControl>

          <Autocomplete
            multiple
            disableCloseOnSelect
            options={allUsers}
            value={selectedUsers}
            onChange={(event, newValue) => {
              setSelectedUsers(newValue);
            }}
            getOptionLabel={(user) => `${user.academic_degree || ''} ${user.first_name || ''} ${user.last_name || ''}`.trim()}
            renderInput={(params) => (
              <TextField
                {...params}
                label={t('statistics.team.selectUsers', 'Benutzer auswählen')}
                placeholder={t('statistics.team.selectUsersPlaceholder', 'Tippen zum Suchen...')}
              />
            )}
            renderTags={(value, getTagProps) =>
              value.map((user, index) => (
                <Chip
                  {...getTagProps({ index })}
                  key={user.id}
                  label={`${user.academic_degree || ''} ${user.first_name || ''} ${user.last_name || ''}`.trim()}
                  size="small"
                />
              ))
            }
            sx={{ flex: 1, minWidth: 250 }}
          />
        </Box>

        <Typography variant="body2" color="text.secondary">
          {t('statistics.team.selectUsersFirst', 'Bitte wählen Sie mindestens einen Benutzer aus.')}
        </Typography>
      </Box>
    );
  }

  if (regionsLoading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: 400 }}>
        <CircularProgress />
      </Box>
    );
  }

  if (!regionsData) {
    return (
      <Box>
        <Typography variant="body2" color="text.secondary">
          {t('statistics.team.modalitaeten.noData', 'Keine Daten verfügbar')}
        </Typography>
      </Box>
    );
  }

  const formatPeriod = (period) => {
    if (!period) return period;
    
    try {
      switch (timeFilter) {
        case 'date':
          if (!isFormatConfigLoaded) return period;
          return formatDateSync(period);
        
        case 'week':
          const weekMatch = period.match(/(\d{4})-W(\d{2})/);
          if (weekMatch) {
            return `KW${weekMatch[2]} - ${weekMatch[1]}`;
          }
          return period;
        
        case 'month':
          const monthMatch = period.match(/(\d{4})-(\d{2})/);
          if (monthMatch) {
            return `${monthMatch[2]}.${monthMatch[1]}`;
          }
          return period;
        
        case 'quarter':
          const quarterMatch = period.match(/(\d{4})-Q(\d)/);
          if (quarterMatch) {
            return `Q${quarterMatch[2]} - ${quarterMatch[1]}`;
          }
          return period;
        
        case 'year':
          return period;
        
        default:
          return period;
      }
    } catch (error) {
      console.error('Error formatting period:', error);
      return period;
    }
  };

  // Erstelle Y-Achsen-Labels (kombiniert User + Kategorie)
  const getYAxisLabels = () => {
    const labels = [];
    selectedUsers.forEach(user => {
      const categories = [];
      if (showIndicationApprovals) categories.push(t('statistics.personal.fallzahlen.indicationApprovals', 'Indikationsprüfungen'));
      if (showMyReports) categories.push(t('statistics.team.fallzahlen.createdReports', 'Erstellte Befunde'));
      if (showForeignSignedReports) categories.push(t('statistics.personal.fallzahlen.foreignSignedReports', '(Fremd-)signierte Befunde'));
      if (showReleasedReports) categories.push(t('statistics.personal.fallzahlen.releasedReports', 'Freigegebene Befunde'));
      
      categories.forEach(cat => {
        const title = user.academic_degree || '';
        const fullName = `${user.first_name} ${user.last_name}`;
        labels.push(`${title ? title + ' ' : ''}${fullName} - ${cat}`);
      });
    });
    return labels;
  };

  return (
    <Box>
      <Typography variant="h6" gutterBottom>
        {t('statistics.team.categories.modalitaeten', 'Team-Modalitäten')}
      </Typography>

      {/* Filter und User-Auswahl in einer Reihe */}
      <Box sx={{ display: 'flex', gap: 2, mb: 3, alignItems: 'flex-start' }}>
        <FormControl sx={{ width: 120, flexShrink: 0 }}>
          <InputLabel>{t('statistics.team.fallzahlen.timePeriod', 'Zeitraum')}</InputLabel>
          <Select
            value={timeFilter}
            onChange={handleTimeFilterChange}
            label={t('statistics.team.fallzahlen.timePeriod', 'Zeitraum')}
          >
            <MenuItem value="date">{t('statistics.team.fallzahlen.day', 'Tag')}</MenuItem>
            <MenuItem value="week">{t('statistics.team.fallzahlen.week', 'Woche')}</MenuItem>
            <MenuItem value="month">{t('statistics.team.fallzahlen.month', 'Monat')}</MenuItem>
            <MenuItem value="quarter">{t('statistics.team.fallzahlen.quarter', 'Quartal')}</MenuItem>
            <MenuItem value="year">{t('statistics.team.fallzahlen.year', 'Jahr')}</MenuItem>
          </Select>
        </FormControl>

        <FormControl sx={{ width: 120, flexShrink: 0 }}>
          <InputLabel>{t('statistics.team.fallzahlen.startPeriod', 'Von')}</InputLabel>
          <Select
            value={startPeriod}
            onChange={handleStartPeriodChange}
            label={t('statistics.team.fallzahlen.startPeriod', 'Von')}
          >
            <MenuItem value="">{t('statistics.team.fallzahlen.all', 'Alle')}</MenuItem>
            {availablePeriods.map((period) => (
              <MenuItem key={period} value={period}>
                {formatPeriod(period)}
              </MenuItem>
            ))}
          </Select>
        </FormControl>

        <FormControl sx={{ width: 120, flexShrink: 0 }}>
          <InputLabel>{t('statistics.team.fallzahlen.endPeriod', 'Bis')}</InputLabel>
          <Select
            value={endPeriod}
            onChange={handleEndPeriodChange}
            label={t('statistics.team.fallzahlen.endPeriod', 'Bis')}
          >
            <MenuItem value="">{t('statistics.team.fallzahlen.all', 'Alle')}</MenuItem>
            {availablePeriods.map((period) => (
              <MenuItem key={period} value={period}>
                {formatPeriod(period)}
              </MenuItem>
            ))}
          </Select>
        </FormControl>

        <Autocomplete
            multiple
          disableCloseOnSelect
          options={allUsers}
          value={selectedUsers}
          onChange={(event, newValue) => {
            setSelectedUsers(newValue);
          }}
          getOptionLabel={(user) => `${user.academic_degree || ''} ${user.first_name || ''} ${user.last_name || ''}`.trim()}
          renderInput={(params) => (
            <TextField
              {...params}
              label={t('statistics.team.selectUsers', 'Benutzer auswählen')}
              placeholder={t('statistics.team.selectUsersPlaceholder', 'Tippen zum Suchen...')}
            />
          )}
          renderTags={(value, getTagProps) =>
            value.map((user, index) => (
                    <Chip 
                {...getTagProps({ index })}
                key={user.id}
                label={`${user.academic_degree || ''} ${user.first_name || ''} ${user.last_name || ''}`.trim()}
                      size="small"
              />
            ))
          }
          sx={{ flex: 1, minWidth: 250 }}
        />
            </Box>

      <Paper sx={{ p: 2 }}>
        <Typography variant="subtitle1" sx={{ mb: 2 }}>
          {t('statistics.team.modalitaeten.chartTitle', 'Team-Modalitäten-Statistik')}
              </Typography>
        
        {!showMyReports && !showForeignSignedReports && !showIndicationApprovals && !showReleasedReports ? (
          <Typography variant="body2" color="text.secondary" sx={{ textAlign: 'center', py: 4 }}>
            {t('statistics.personal.fallzahlen.noSeriesSelected', 'Bitte wählen Sie mindestens eine Kategorie aus')}
          </Typography>
        ) : chartData.length > 0 ? (
          <Box sx={{ display: 'flex', width: '100%', position: 'relative' }}>
            {/* Custom Y-Achsen Labels - nur User-Namen */}
            <Box sx={{ 
              width: '300px', 
              height: Math.max(400, selectedUsers.length * 160),
              display: 'flex',
              flexDirection: 'column',
              flexShrink: 0,
              pt: '15px',
              pb: '70px'
            }}>
              {selectedUsers.map((user, index) => {
                const totalHeight = Math.max(400, selectedUsers.length * 160);
                const chartMarginTop = 20;
                const chartMarginBottom = 50;
                const yAxisHeight = totalHeight - chartMarginTop - chartMarginBottom;
                const userHeight = yAxisHeight / selectedUsers.length;
                
                const title = user.academic_degree || '';
                const fullName = `${user.first_name} ${user.last_name}`;
                
                return (
                  <Box 
                    key={user.id}
                  sx={{
                      height: `${userHeight}px`,
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'flex-end',
                      pr: 2,
                      borderBottom: index < selectedUsers.length - 1 ? '1px solid rgba(0,0,0,0.05)' : 'none'
                    }}
                  >
                    <Typography 
                      variant="body2" 
                  sx={{
                        textAlign: 'right',
                        fontSize: '0.875rem',
                        lineHeight: 1.3
                      }}
                    >
                      <Box component="span" sx={{ fontWeight: 600, display: 'block' }}>
                        {title}
                </Box>
                      <Box component="span" sx={{ display: 'block' }}>
                        {fullName}
            </Box>
                  </Typography>
                </Box>
                );
              })}
            </Box>

            {/* SVG Pattern Definitions für Kategorien */}
            <svg width="0" height="0" style={{ position: 'absolute' }}>
              <defs>
                {availableRegions.map((modality, index) => {
                  const color = getModalityColor(index);
                  return (
                    <React.Fragment key={modality}>
                      {/* Gepunktet für Indikationsprüfungen */}
                      <pattern id={`dots-${modality}`} patternUnits="userSpaceOnUse" width="12" height="12">
                        <rect width="12" height="12" fill={color} />
                        <circle cx="3" cy="3" r="2.5" fill="white" />
                        <circle cx="9" cy="3" r="2.5" fill="white" />
                        <circle cx="3" cy="9" r="2.5" fill="white" />
                        <circle cx="9" cy="9" r="2.5" fill="white" />
                      </pattern>
                      
                      {/* Horizontal liniert für Erstellte Befunde */}
                      <pattern id={`horizontal-${modality}`} patternUnits="userSpaceOnUse" width="8" height="8">
                        <rect width="8" height="8" fill={color} />
                        <line x1="0" y1="2" x2="8" y2="2" stroke="white" strokeWidth="2" />
                        <line x1="0" y1="6" x2="8" y2="6" stroke="white" strokeWidth="2" />
                      </pattern>
                      
                      {/* Diagonal liniert für Signierte Befunde */}
                      <pattern id={`diagonal-${modality}`} patternUnits="userSpaceOnUse" width="12" height="12">
                        <rect width="12" height="12" fill={color} />
                        <line x1="0" y1="0" x2="12" y2="12" stroke="white" strokeWidth="2.5" />
                        <line x1="-3" y1="9" x2="3" y2="15" stroke="white" strokeWidth="2.5" />
                        <line x1="9" y1="-3" x2="15" y2="3" stroke="white" strokeWidth="2.5" />
                      </pattern>
                      
                      {/* Vertikal liniert für Freigegebene Befunde */}
                      <pattern id={`vertical-${modality}`} patternUnits="userSpaceOnUse" width="8" height="8">
                        <rect width="8" height="8" fill={color} />
                        <line x1="2" y1="0" x2="2" y2="8" stroke="white" strokeWidth="2" />
                        <line x1="6" y1="0" x2="6" y2="8" stroke="white" strokeWidth="2" />
                      </pattern>
                    </React.Fragment>
                  );
                })}
              </defs>
            </svg>

            {/* Stacked Bar Chart */}
            <Box 
              ref={chartRef} 
                  sx={{
                flex: 1, 
                height: Math.max(400, selectedUsers.length * 160),
                '& .MuiBarElement-root': {
                  transition: 'opacity 0.2s, filter 0.2s',
                      cursor: 'pointer',
                },
                '& .MuiBarElement-root.highlighted': {
                  filter: 'brightness(1.2)',
                  opacity: 1,
                },
                '& .MuiBarElement-root:not(.highlighted)': {
                  opacity: highlightedSeries ? 0.3 : 1,
                }
              }}
            >
              <BarChart
                dataset={chartData}
                layout="horizontal"
                yAxis={[{ 
                  dataKey: 'label',
                  scaleType: 'band',
                  categoryGapRatio: 0.3,
                  barGapRatio: 0.1
                }]}
                xAxis={[{
                  min: 0
                }]}
                tooltip={{
                  trigger: 'item'
                }}
                  sx={{
                  '.MuiChartsLegend-root': {
                    display: 'none !important'
                    },
                  '.MuiChartsAxis-left .MuiChartsAxis-tickLabel': {
                      display: 'none !important'
                  },
                  '.MuiChartsAxis-left .MuiChartsAxis-line': {
                    display: 'none !important'
                  },
                  '& .MuiBarElement-root': {
                    transition: 'opacity 0.2s ease-in-out, filter 0.2s ease-in-out',
                    cursor: 'pointer',
                    ...(highlightedSeries && {
                      opacity: 0.15,
                      filter: 'grayscale(80%)'
                    })
                  },
                  '& .MuiBarElement-root.highlighted': {
                    opacity: '1 !important',
                    filter: 'none !important'
                  }
                }}
                series={availableRegions.map((modality, index) => ({
                  type: 'bar',
                  stack: 'total',
                  id: modality,
                  dataKey: modality,
                  label: modality,
                  color: getModalityColor(index),
                  valueFormatter: (value) => value ? `${value}` : ''
                }))}
                margin={{ top: 20, right: 30, bottom: 50, left: 10 }}
                grid={{ vertical: true, horizontal: false }}
              />
            </Box>
          </Box>
        ) : (
          <Typography variant="body2" color="text.secondary" sx={{ textAlign: 'center', py: 4 }}>
            {t('statistics.personal.fallzahlen.noChartData', 'Keine Daten für die ausgewählten Filter verfügbar')}
                  </Typography>
              )}

        {/* Checkboxen für Kategorien (ohne Farbe) */}
        <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap', justifyContent: 'flex-end', mt: 2 }}>
          <FormControlLabel
            control={
              <Checkbox 
                checked={showIndicationApprovals} 
                onChange={(e) => setShowIndicationApprovals(e.target.checked)}
              />
            }
            label={
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                <Box sx={{ width: 16, height: 16, border: '1px solid var(--rd-border)', borderRadius: '2px', background: 'radial-gradient(circle, var(--rd-text-secondary) 20%, transparent 20%)' }} />
                {t('statistics.personal.fallzahlen.indicationApprovals', 'Indikationsprüfungen')}
              </Box>
            }
          />
          <FormControlLabel
            control={
              <Checkbox 
                checked={showMyReports} 
                onChange={(e) => setShowMyReports(e.target.checked)}
              />
            }
            label={
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                <Box sx={{ width: 16, height: 16, border: '1px solid var(--rd-border)', borderRadius: '2px', background: 'repeating-linear-gradient(0deg, var(--rd-text-secondary), var(--rd-text-secondary) 2px, transparent 2px, transparent 4px)' }} />
                {t('statistics.team.fallzahlen.createdReports', 'Erstellte Befunde')}
              </Box>
            }
          />
          <FormControlLabel
            control={
              <Checkbox 
                checked={showForeignSignedReports} 
                onChange={(e) => setShowForeignSignedReports(e.target.checked)}
              />
            }
            label={
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                <Box sx={{ width: 16, height: 16, border: '1px solid var(--rd-border)', borderRadius: '2px', background: 'repeating-linear-gradient(45deg, var(--rd-text-secondary), var(--rd-text-secondary) 2px, transparent 2px, transparent 4px)' }} />
                {t('statistics.personal.fallzahlen.foreignSignedReports', '(Fremd-)signierte Befunde')}
              </Box>
            }
          />
          <FormControlLabel
            control={
              <Checkbox 
                checked={showReleasedReports} 
                onChange={(e) => setShowReleasedReports(e.target.checked)}
              />
            }
            label={
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                <Box sx={{ width: 16, height: 16, border: '1px solid var(--rd-border)', borderRadius: '2px', background: 'repeating-linear-gradient(90deg, var(--rd-text-secondary), var(--rd-text-secondary) 2px, transparent 2px, transparent 4px)' }} />
                {t('statistics.personal.fallzahlen.releasedReports', 'Freigegebene Befunde')}
              </Box>
            }
          />
        </Box>

        {/* Modalitäten-Legende */}
        <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap', justifyContent: 'center', mt: 3, pt: 2, borderTop: 1, borderColor: 'divider' }}>
          <Typography variant="subtitle2" sx={{ width: '100%', textAlign: 'center', mb: 1 }}>
            {t('statistics.personal.pdf.legend', 'Legende')} - {t('statistics.personal.modalitaeten.selectModalities', 'Modalitäten')}:
          </Typography>
          {availableRegions.map((modality, index) => (
            <Chip 
              key={modality}
              label={modality}
              size="small"
              sx={{ 
                backgroundColor: getModalityColor(index),
                color: 'white',
                fontWeight: 500
              }}
            />
          ))}
        </Box>
      </Paper>
    </Box>
  );
});

Regions.displayName = 'Regions';

export default Regions;
